import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import clss from 'classnames'
import Button from '../Button'
import { DioxContextProvider } from '../../../hooks/useDiox'

const DialogStyled = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  background: linear-gradient(
      180deg,
      rgba(224, 194, 134, 0.2) 0%,
      rgba(224, 194, 134, 0.082) 8.52%,
      rgba(224, 194, 134, 0.044) 14.28%,
      rgba(224, 194, 134, 0) 21.25%
    ),
    #1a1919;
  border: 1px solid #3a3939;
  box-shadow: 0px 12px 32px rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 32px;
  z-index: 999;
  min-width: 420px;
  transform-origin: left;
  transition: transform 0.2s ease-in-out;
  transform: translate(-50%, -50%);
  &.inner-width {
    min-width: unset;
  }
  /* animation: scaleShow 0.5s forwards; */
  &.hide {
    /* animation: scaleHide 0.5s forwards; */
  }
  .content {
    padding: 0 16px;
  }
  &.unset-title {
    padding: 32px 48px;
    .content {
      padding: 0;
    }
  }
  .title {
    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
    color: #ffffff;
  }
  button {
    min-width: 120px;
    font-weight: 500;
    font-size: 15px;
    line-height: 22px;
  }
  .ok-btn {
    margin: 0 auto;
  }
  .cancel-btn {
    margin-right: 16px;
  }
  .confirm-btns {
    display: flex;
    align-items: center;
    justify-content: end;
  }
  @keyframes scaleShow {
    0% {
      transform: scale(0) translate(-50%, -50%);
    }
    60% {
      transform: scale(1.2) translate(-50%, -50%);
    }
    100% {
      transform: scale(1) translate(-50%, -50%);
    }
  }
  @keyframes scaleHide {
    0% {
      transform: scale(1) translate(-50%, -50%);
    }
    60% {
      transform: scale(1.2) translate(-50%, -50%);
    }
    100% {
      transform: scale(0) translate(-50%, -50%);
    }
  }
`
type DialogProps = {
  className?: string
  content: JSX.Element
  cancelText?: string
  confirmText?: string
  okText?: string
  title?: string
  ref?: React.Ref<unknown>
  confirmDisabled?: boolean
  canClickMarkClose?: boolean // 点击遮罩层关闭
  onCancel?: (r?: unknown) => void
  onConfirm?: (r?: unknown) => void
}

const Dialog = forwardRef(function Dialog(props: DialogProps, ref) {
  const {
    content,
    title,
    cancelText,
    onCancel,
    onConfirm,
    confirmText,
    okText,
    className,
    confirmDisabled,
    canClickMarkClose = true,
  } = props
  const [visibale, setVisibale] = useState(false)

  useImperativeHandle(ref, () => ({
    close: handleClose,
  }))

  const handleClose = useCallback(() => {
    setVisibale(true)
    onCancel && onCancel()
  }, [onCancel])

  const handleConfirm = useCallback(async () => {
    if (onConfirm) {
      await onConfirm()
    }
    setVisibale(true)
  }, [onCancel, onConfirm])

  const renderBtn = useMemo(() => {
    if (okText)
      return (
        <Button className="ok-btn" type="primary" onClick={handleClose}>
          {okText}
        </Button>
      )
    if (!confirmText) return ''
    return (
      <div className="confirm-btns">
        {cancelText && (
          <Button className="cancel-btn" type="outline" onClick={handleClose}>
            {cancelText}
          </Button>
        )}
        <Button disabled={confirmDisabled} type="primary" onClick={handleConfirm}>
          {confirmText}
        </Button>
      </div>
    )
  }, [okText, confirmDisabled, cancelText, confirmText, handleClose, handleConfirm])

  return (
    <>
      <div className="mark" onClick={canClickMarkClose ? handleClose : undefined} />
      <DialogStyled className={clss({ 'unset-title': !title, hide: visibale }, className)}>
        {title && <h3 className="title">{title}</h3>}
        <div className="content">{content}</div>
        {renderBtn}
      </DialogStyled>
    </>
  )
})

export default function dialog(opt: DialogProps) {
  const container = document.createElement('div')
  const { onConfirm, onCancel, ...opts } = opt

  document.body.appendChild(container)
  const decorateConfirm = (r: unknown) => {
    onConfirm && onConfirm(r)
    removeDom()
  }
  const decorateCancel = (r: unknown) => {
    onCancel && onCancel(r)
    removeDom()
  }
  const removeDom = () =>
    setTimeout(() => {
      container && container.parentNode === document.body && document.body.removeChild(container)
    }) // animation need delay time
  ReactDOM.render(
    <DioxContextProvider>
      <Dialog {...opts} onCancel={decorateCancel} onConfirm={decorateConfirm} />
    </DioxContextProvider>,
    container,
  )
}
